/* AVERELLE motion (own code, 2 Nov 2026).
   Lightweight reveal-on-scroll for content below the fold. Elements already visible
   on load are never hidden, so there is no flash. Respects prefers-reduced-motion.
   Opt-in hooks: [data-avr-reveal] (single element), [data-avr-reveal-stagger] (children
   appear one after another). Automatic targets: non-AVERELLE sections in <main> and
   blog post cards (staggered), mirroring the old storefront's animation defaults. */
(() => {
  'use strict';
  if (window.__avrMotionReady) return;
  window.__avrMotionReady = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceMotion.matches || !('IntersectionObserver' in window)) return;

  const STAGGER_STEP = 0.08;
  const STAGGER_MAX = 6;

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('is-revealed');
      observer.unobserve(entry.target);
    }
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

  const safeQuery = (root, selector) => {
    try {
      return [...root.querySelectorAll(selector)];
    } catch (error) {
      return [];
    }
  };

  const isBelowFold = (element) => element.getBoundingClientRect().top > window.innerHeight * 0.92;

  const prepare = (element, delay = 0) => {
    if (!(element instanceof HTMLElement) || element.dataset.avrRevealReady) return;
    element.dataset.avrRevealReady = 'true';
    if (!isBelowFold(element)) return;
    if (delay) element.style.setProperty('--avr-reveal-delay', `${delay}s`);
    element.classList.add('avr-reveal');
    observer.observe(element);
  };

  const collect = (root = document) => {
    safeQuery(root, '[data-avr-reveal]').forEach((element) => prepare(element));

    safeQuery(root, '[data-avr-reveal-stagger]').forEach((group) => {
      [...group.children].forEach((child, index) => prepare(child, Math.min(index, STAGGER_MAX) * STAGGER_STEP));
    });

    safeQuery(root, '.blog-post-card').forEach((card) => {
      const siblings = card.parentElement ? [...card.parentElement.children] : [card];
      const index = Math.max(0, siblings.indexOf(card));
      prepare(card, Math.min(index % 4, STAGGER_MAX) * STAGGER_STEP);
    });

    safeQuery(root, '#MainContent > .shopify-section').forEach((section) => {
      if (section.querySelector('.avr, .avr-section, product-component, .product-information, .blog-post-card')) return;
      if (section.matches('[class*="main-cart"], [id*="cart"]')) return;
      prepare(section);
    });
  };

  const start = () => collect(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) collect(event.target);
  });
})();

/* AVERELLE card quick add + desktop cart-drawer dismissal (own code, 2 Nov 2026).
   - Product cards: the "+" form (snippets/avr-product-card) adds one unit through Horizon's cart
     actions, which refresh and open the cart drawer; plain form post as fallback.
   - Desktop: a click on the dimmed page next to the open cart drawer closes it (avr-qa-2.css §4). */
(() => {
  'use strict';
  if (window.__avrQuickAddReady) return;
  window.__avrQuickAddReady = true;

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-avr-quick-add]')) return;
    const actions = window.Shopify && window.Shopify.actions;
    if (!actions || typeof actions.updateCart !== 'function') return; // normal form post
    event.preventDefault();
    const button = form.querySelector('button');
    const id = form.querySelector('[name="id"]').value;
    if (button) button.disabled = true;
    Promise.resolve(actions.updateCart({ lines: [{ merchandiseId: 'gid://shopify/ProductVariant/' + id, quantity: 1 }] }))
      .catch(() => form.submit())
      .finally(() => { if (button) button.disabled = false; });
  });

  document.addEventListener('click', (event) => {
    if (event.target !== document.body || window.innerWidth < 990) return;
    const drawer = document.querySelector('#cart-drawer[open]');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  });
})();
